import { computed, onMounted, ref, watch } from 'vue'
import { useCookie } from '#app'
import { useRoute } from '#imports'
import { normalizeToCamelCase } from '~/utils/stringNormalize'
import { useGenderMenuItems } from '~/composables/useGenderMenuItems'
import { useCurrentShop } from '#storefront/composables'

const oldCookieValue = ref<string | null>(null)

export const useShopGenderCookie = () => {
  const route = useRoute()
  const shop = useCurrentShop()
  const { scope, categories } = route.params
  const isHomepage = route.path === '/' || route.path === `/${shop.value?.path}`
  const { isValidGender } = useGenderMenuItems()
  const countryCode = computed(() => shop.value?.countryCode)

  const cookie = useCookie(`shop_gender_${countryCode.value}`, {
    default: () => '',
    watch: true,
    maxAge: 30 * 24 * 60 * 60,
    httpOnly: false,
    secure: process.env.NUXT_PUBLIC_APP_ENV === 'production',
    sameSite: 'strict',
    path: '/',
  })

  const updateCookie = (newValue: string) => {
    const normalized = normalizeToCamelCase(newValue)
    if (cookie.value !== normalized) {
      oldCookieValue.value = cookie.value || null
      cookie.value = normalized
    }
  }

  onMounted(() => {
    if (!isValidGender(cookie.value)) {
      cookie.value = ''
    }

    if (scope && isValidGender(scope as string)) {
      updateCookie(scope as string)
    }
  })

  watch(
    () => [scope, categories],
    ([newScope, newCategories]) => {
      if (
        newScope &&
        isValidGender(newScope as string) &&
        normalizeToCamelCase(newScope as string) !== cookie.value
      ) {
        updateCookie(newScope as string)
      }

      if (
        newCategories &&
        newCategories?.[0] &&
        isValidGender(newCategories[0]) &&
        !isHomepage
      ) {
        updateCookie(newCategories[0])
      }
    },
    {
      immediate: true,
    },
  )

  return {
    cookie,
    oldCookieValue,
    updateCookie,
  }
}
